import React, { useEffect, useState } from "react";
import { Plus, Trash2, Copy, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { todayInput } from "@/lib/date";

const newBlock = () => ({ start_time: "", end_time: "", label: "", spots: 1 });
const newEvent = (date = "", description = "", blocks = [newBlock()]) => ({ event_date: date, description, blocks });
const cloneBlocks = (blocks) => blocks.map(({ start_time, end_time, label, spots }) => ({ start_time, end_time, label, spots }));

const addDays = (s, n) => {
  if (!s) return "";
  const [y, m, d] = s.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};

function BlockRow({ b, onChange, onRemove, canRemove, testId }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-[7rem_7rem_1fr_5.5rem_2rem] sm:items-end" data-testid={testId}>
      <div><Label className="text-[11px] text-slate-500">Start</Label><Input type="time" value={b.start_time} onChange={(e) => onChange({ start_time: e.target.value })} data-testid={`${testId}-start`} /></div>
      <div><Label className="text-[11px] text-slate-500">End</Label><Input type="time" value={b.end_time} onChange={(e) => onChange({ end_time: e.target.value })} data-testid={`${testId}-end`} /></div>
      <div className="col-span-2 sm:col-span-1"><Label className="text-[11px] text-slate-500">Label (optional)</Label><Input value={b.label} maxLength={60} placeholder="e.g. Setup" onChange={(e) => onChange({ label: e.target.value })} data-testid={`${testId}-label`} /></div>
      <div><Label className="text-[11px] text-slate-500">Needed</Label><Input type="number" min={1} max={500} value={b.spots} onChange={(e) => onChange({ spots: e.target.value })} data-testid={`${testId}-spots`} /></div>
      <Button type="button" size="icon" variant="ghost" className="h-9 w-9 text-red-500" disabled={!canRemove} onClick={onRemove} data-testid={`${testId}-remove`}><Trash2 className="h-4 w-4" /></Button>
    </div>
  );
}

function EventEditor({ ev, idx, total, update, remove, isNew }) {
  const setBlock = (bi, patch) => update({ blocks: ev.blocks.map((b, i) => (i === bi ? { ...b, ...patch } : b)) });
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-3" data-testid={`vol-event-${idx}`}>
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">Date</Label><Input type="date" min={isNew ? todayInput() : undefined} value={ev.event_date} onChange={(e) => update({ event_date: e.target.value })} className="w-44" data-testid={`vol-event-${idx}-date`} /></div>
        <div className="min-w-[12rem] flex-1"><Label className="text-xs">Description</Label><Input value={ev.description} maxLength={1000} placeholder="e.g. Home game vs. Reagan — concession stand" onChange={(e) => update({ description: e.target.value })} data-testid={`vol-event-${idx}-description`} /></div>
        {total > 1 && <Button type="button" size="sm" variant="ghost" className="text-red-500" onClick={remove} data-testid={`vol-event-${idx}-remove`}><Trash2 className="mr-1 h-4 w-4" /> Remove date</Button>}
      </div>
      <div className="space-y-2">
        {ev.blocks.map((b, bi) => (
          <BlockRow key={bi} b={b} testId={`vol-event-${idx}-block-${bi}`} canRemove={ev.blocks.length > 1}
            onChange={(p) => setBlock(bi, p)} onRemove={() => update({ blocks: ev.blocks.filter((_, i) => i !== bi) })} />
        ))}
      </div>
      <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => update({ blocks: [...ev.blocks, newBlock()] })} data-testid={`vol-event-${idx}-add-block`}><Plus className="mr-1 h-3.5 w-3.5" /> Add time block</Button>
    </div>
  );
}

// Create / edit a volunteer sign-up sheet: title, details, and one or more dates with time blocks.
export default function VolunteerSheetDialog({ open, onClose, editing, onSaved }) {
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [events, setEvents] = useState([newEvent()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(editing?.title || "");
    setDetails(editing?.details || "");
    setEvents(editing ? editing.events.map((e) => ({ ...e, blocks: e.blocks.map((b) => ({ id: b.id, start_time: b.start_time, end_time: b.end_time, label: b.label || "", spots: b.spots })) })) : [newEvent()]);
  }, [open, editing]);

  const updateEvent = (i, patch) => setEvents((evs) => evs.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  const addDate = () => setEvents((evs) => { const last = evs[evs.length - 1]; return [...evs, newEvent(addDays(last.event_date, 7), last.description, cloneBlocks(last.blocks))]; });
  const copyBlocks = () => setEvents((evs) => evs.map((e, i) => (i === 0 ? e : { ...e, blocks: cloneBlocks(evs[0].blocks) })));

  const save = async () => {
    setSaving(true);
    try {
      const body = { title, details, events: events.map((e) => ({ ...e, blocks: e.blocks.map((b) => ({ ...b, spots: Number(b.spots) || 0 })) })) };
      if (editing) await api.put(`/volunteers/sheets/${editing.id}`, body);
      else await api.post("/volunteers/sheets", body);
      toast.success(editing ? "Sign-up sheet updated" : "Sign-up sheet posted — your team has been notified");
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90dvh] max-w-3xl overflow-y-auto" data-testid="vol-sheet-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? "Edit volunteer sign-up" : "New volunteer sign-up"}</DialogTitle>
          <DialogDescription>Add one date or a series. Each date gets its own description and time blocks with the number of volunteers needed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Title</Label><Input value={title} maxLength={120} placeholder="e.g. Concession Stand" onChange={(e) => setTitle(e.target.value)} data-testid="vol-sheet-title" /></div>
          <div><Label>Details <span className="text-slate-400">(optional)</span></Label><Textarea rows={2} value={details} maxLength={2000} onChange={(e) => setDetails(e.target.value)} data-testid="vol-sheet-details" /></div>
          {editing && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">Removing a time block also removes its sign-ups.</p>}
          {events.map((ev, i) => (
            <EventEditor key={i} ev={ev} idx={i} total={events.length} isNew={!ev.id} update={(p) => updateEvent(i, p)} remove={() => setEvents((evs) => evs.filter((_, j) => j !== i))} />
          ))}
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" className="rounded-full" onClick={addDate} data-testid="vol-add-date"><Plus className="mr-1 h-4 w-4" /> Add date</Button>
            {events.length > 1 && <Button type="button" variant="ghost" className="rounded-full" onClick={copyBlocks} data-testid="vol-copy-blocks"><Copy className="mr-1 h-4 w-4" /> Copy first date's blocks to all dates</Button>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-full" onClick={onClose} data-testid="vol-sheet-cancel">Cancel</Button>
          <Button className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={saving} onClick={save} data-testid="vol-sheet-save">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editing ? "Save changes" : "Post sign-up"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
